import React, { useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api'
import { useAppLanguage } from '../i18n'

type Stats = {
  totals: { media: number; embeddings: number }
  by_status: Record<string, number>
  by_course: Record<string, number>
  recent: { id: number; title: string; status: string; course_id?: string | null; course_title?: string | null; created_at: string }[]
  activity_7d: { day: string; count: number }[]
}

const statusPalette: Record<string, string> = {
  uploaded: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200',
  transcribed: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-200',
  indexed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200',
  error: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200',
}

export default function DashboardPage() {
  const { t, language } = useAppLanguage()
  const [stats, setStats] = useState<Stats | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const locale = useMemo(() => {
    if (language === 'en') return 'en-US'
    if (language === 'es') return 'es-ES'
    return 'fr-FR'
  }, [language])

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/stats')
        setStats(data)
      } catch (e: any) {
        setError(e?.response?.data?.detail || t.dashboard.loadStatsError)
      } finally {
        setLoading(false)
      }
    })()
  }, [t.dashboard.loadStatsError])

  const totalCourses = useMemo(() => Object.keys(stats?.by_course || {}).length, [stats])
  const totalStatuses = useMemo(() => Object.keys(stats?.by_status || {}).length, [stats])
  const activityTotal = useMemo(() => (stats?.activity_7d || []).reduce((acc, item) => acc + item.count, 0), [stats])

  if (error) {
    return (
      <div className="rounded-3xl border border-red-200 bg-red-50 p-6 text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
        {error}
      </div>
    )
  }

  return (
    <div className="relative">
      <div
        aria-hidden
        className="absolute inset-0 -z-20 bg-gradient-to-b from-emerald-50 via-white to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-900"
      />
      <div aria-hidden className="absolute inset-x-0 top-0 -z-30 h-[420px] bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.25),_transparent_65%)]" />
      <div aria-hidden className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_20%_20%,rgba(236,72,153,0.15),transparent_60%),radial-gradient(circle_at_80%_10%,rgba(168,85,247,0.18),transparent_55%)] dark:bg-[radial-gradient(circle_at_15%_20%,rgba(236,72,153,0.12),transparent_55%),radial-gradient(circle_at_85%_15%,rgba(168,85,247,0.15),transparent_50%)]" />

      <div className="relative mx-auto flex max-w-[1600px] flex-col gap-4 py-3 sm:py-6">

        {loading && (
          <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div
                key={index}
                className="h-32 animate-pulse rounded-3xl border border-emerald-100/70 bg-white/60 dark:border-emerald-900/40 dark:bg-zinc-900/60"
              />
            ))}
          </section>
        )}

        {stats && (
          <>
            <section className="grid gap-4 grid-cols-2 lg:grid-cols-4">
              <StatCard
                title={t.dashboard.cards.indexedMediaTitle}
                value={stats.totals.media}
                description={t.dashboard.cards.indexedMediaDescription}
                locale={locale}
              />
              <StatCard
                title={t.dashboard.cards.embeddingsTitle}
                value={stats.totals.embeddings}
                description={t.dashboard.cards.embeddingsDescription}
                locale={locale}
              />
              <StatCard
                title={t.dashboard.cards.trackedStatusesTitle}
                value={totalStatuses}
                description={t.dashboard.cards.trackedStatusesDescription}
                locale={locale}
              />
              <StatCard
                title={t.dashboard.cards.linkedCoursesTitle}
                value={totalCourses}
                description={t.dashboard.cards.linkedCoursesDescription}
                locale={locale}
              />
            </section>

            <section className="grid gap-4 lg:grid-cols-[1.1fr_0.9fr]">
              {/* Derniers médias */}
              <div className="rounded-3xl border border-emerald-100/80 bg-white/90 p-4 shadow-xl shadow-emerald-500/10 dark:border-emerald-900/50 dark:bg-zinc-950/90 overflow-hidden">
                <div className="mb-4">
                  <h2 className="text-base font-semibold text-zinc-900 dark:text-white truncate">{t.dashboard.recent.title}</h2>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 truncate">{t.dashboard.recent.description}</p>
                </div>

                {/* Mobile: cards empilées */}
                <div className="flex flex-col divide-y divide-zinc-100 dark:divide-zinc-800 lg:hidden">
                  {stats.recent?.length ? (
                    stats.recent.slice(0, 6).map((item) => (
                      <div key={item.id} className="py-2.5 flex items-center gap-3 min-w-0">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-zinc-800 dark:text-zinc-100 truncate">{item.title}</p>
                          <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-0.5">{formatDate(item.created_at, locale)}</p>
                        </div>
                        <span className={`shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${statusPalette[item.status] || 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800/80 dark:text-zinc-300'}`}>
                          <span className="inline-block h-1.5 w-1.5 rounded-full bg-current" aria-hidden />
                          {formatStatus(item.status)}
                        </span>
                      </div>
                    ))
                  ) : (
                    <p className="py-6 text-center text-sm text-zinc-500 dark:text-zinc-400">{t.dashboard.recent.empty}</p>
                  )}
                </div>

                {/* Desktop: table */}
                <div className="hidden lg:block rounded-2xl border border-zinc-100/80 bg-white/95 text-sm shadow-sm dark:border-zinc-800 dark:bg-zinc-900/90">
                  <table className="w-full border-collapse">
                    <thead className="bg-emerald-50/50 border-b border-emerald-100/70 dark:bg-zinc-900/50 dark:border-emerald-900/40">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">{t.dashboard.recent.table.title}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">{t.dashboard.recent.table.status}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">{t.dashboard.recent.table.course}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">{t.dashboard.recent.table.date}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.recent?.length ? (
                        stats.recent.slice(0, 6).map((item) => (
                          <tr key={item.id} className="border-t border-zinc-100/80 dark:border-zinc-800/80">
                            <td className="px-4 py-3 font-medium text-zinc-800 dark:text-zinc-100 max-w-[160px] truncate">{item.title}</td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${statusPalette[item.status] || 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800/80 dark:text-zinc-300'}`}>
                                <span className="inline-block h-1.5 w-1.5 rounded-full bg-current" aria-hidden />
                                {formatStatus(item.status)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300 max-w-[120px] truncate">{item.course_title ?? item.course_id ?? '—'}</td>
                            <td className="px-4 py-3 text-zinc-500 dark:text-zinc-400 whitespace-nowrap text-xs">{formatDate(item.created_at, locale)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={4} className="px-4 py-6 text-center text-sm text-zinc-500 dark:text-zinc-400">{t.dashboard.recent.empty}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Activité + Statuts */}
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
                {/* Activité 7 jours */}
                <aside className="rounded-3xl border border-zinc-200/70 bg-white/95 p-4 shadow-lg dark:border-zinc-800 dark:bg-zinc-900/90">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-300 mb-2">
                    {t.dashboard.activity.title}
                  </h3>
                  <div className="text-2xl font-bold text-zinc-900 dark:text-white mb-1">{activityTotal}</div>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-3">{t.dashboard.activity.totalDescription}</p>
                  <div className="space-y-1.5">
                    {(stats.activity_7d || []).map((entry) => {
                      const max = Math.max(...(stats.activity_7d || []).map(e => e.count), 1)
                      const pct = Math.round((entry.count / max) * 100)
                      return (
                        <div key={entry.day} className="grid grid-cols-[3rem_1fr_1rem] items-center gap-2 text-xs text-zinc-600 dark:text-zinc-300">
                          <span className="truncate">{formatDay(entry.day, locale)}</span>
                          <div className="h-1.5 rounded-full bg-zinc-100 dark:bg-zinc-800 overflow-hidden w-full">
                            <div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-right font-semibold text-zinc-900 dark:text-white tabular-nums">{entry.count}</span>
                        </div>
                      )
                    })}
                  </div>
                </aside>

                {/* Statuts */}
                <aside className="rounded-3xl border border-zinc-200/70 bg-white/95 p-4 shadow-lg dark:border-zinc-800 dark:bg-zinc-900/90">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-300 mb-3">
                    {t.dashboard.statuses.title}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(stats.by_status).map(([status, value]) => (
                      <span key={status} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200/70 bg-emerald-50/60 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200">
                        {formatStatus(status)}
                        <span className="rounded-full bg-emerald-500/20 px-1.5 py-0.5 text-[10px] font-bold">{value}</span>
                      </span>
                    ))}
                    {!Object.keys(stats.by_status || {}).length && (
                      <span className="text-sm text-zinc-500 dark:text-zinc-400">{t.dashboard.statuses.empty}</span>
                    )}
                  </div>
                </aside>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  )
}

function StatCard({
  title,
  value,
  description,
  locale,
}: {
  title: string
  value: number
  description: string
  locale: string
}) {
  return (
    <div className="relative overflow-hidden rounded-3xl border border-emerald-100/70 bg-white/90 p-4 sm:p-6 shadow-lg shadow-emerald-500/10 transition hover:-translate-y-0.5 hover:shadow-emerald-500/20 dark:border-emerald-900/50 dark:bg-zinc-950/90">
      <div aria-hidden className="absolute -right-12 -top-12 h-32 w-32 rounded-full bg-emerald-400/20 blur-3xl dark:bg-emerald-500/30" />
      <div className="relative space-y-2">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-emerald-600 dark:text-emerald-300">{title}</p>
        <div className="text-3xl font-bold text-zinc-900 dark:text-white">{value.toLocaleString(locale)}</div>
        <p className="text-sm text-zinc-500 dark:text-zinc-400">{description}</p>
      </div>
    </div>
  )
}

function formatStatus(status: string) {
  if (!status) return '—'
  return status
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
}

function formatDate(date: string, locale: string) {
  return new Date(date).toLocaleString(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatDay(day: string, locale: string) {
  const parsed = new Date(day)
  if (Number.isNaN(parsed.getTime())) return day
  return parsed.toLocaleDateString(locale, { weekday: 'short', day: '2-digit' })
}
